import type { ModifyRequest } from '@/lib/dd-queue/types'
import type { PimRecord } from '@/lib/dd-queue/types/subjectMutation'
import { fmtMoney } from '@/lib/dd-queue/helpers'
import { modifyPartText } from '@/lib/sales-workflow/modifyParts'
import { getProduct } from '@/lib/dd-queue/pimCatalog'
import { formatMountingHoles, WHITE_INK_LABELS } from '@/lib/dd-queue/finishingFromSpec'

/**
 * DD-CHG-CARD-001 — the Modify form sends catalog picks as `{ id, label }` refs (substrate,
 * laminate, graphic method, vinyl type, rounded corners, hole location / diameter) where the
 * prototype sent strings; read them the way New Product intake does (`spec.graphic_method?.label`).
 * Returns null for empty values so callers can drop the row.
 */
export function refLabel(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === 'string') return value.trim() || null
  if (typeof value === 'number') return String(value)
  if (typeof value === 'object') {
    const ref = value as { label?: unknown; id?: unknown }
    const label = typeof ref.label === 'string' ? ref.label.trim() : ''
    if (label) return label
    const id = typeof ref.id === 'string' ? ref.id.trim() : ''
    return id || null
  }
  return null
}

/** The id of a `{ id, label }` ref, or the string itself (ids feed the mounting-holes wording). */
export function refId(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === 'string') return value.trim() || null
  if (typeof value === 'object') {
    const id = (value as { id?: unknown }).id
    return typeof id === 'string' && id.trim() ? id.trim() : null
  }
  return null
}

/** "all 4 corners, ⌀ 0.375\"" — same wording as the attribute / packet (finishingFromSpec). */
export function mountingHolesText(value: unknown): string | null {
  if (value == null || typeof value !== 'object') return null
  const holes = value as { location?: unknown; diameter?: unknown }
  const text = formatMountingHoles(refId(holes.location) ?? '', refId(holes.diameter) ?? '')
  return text || null
}

/** White-ink codes → the labels the packet prints ("Flood White, Spot White"). */
export function whiteInkText(value: unknown): string | null {
  if (!Array.isArray(value)) return null
  const labels = value
    .filter((c): c is string => typeof c === 'string' && c.trim() !== '')
    .map((c) => WHITE_INK_LABELS[c] ?? c)
  return labels.length ? labels.join(', ') : null
}

function row(term: string, detail: string | null): ChangeDetailRow[] {
  return detail ? [{ term, detail }] : []
}

/** Reference PMAView.tsx L15–21 — change type labels in the Change Request banner. */
export const CHANGE_LABELS: Record<string, string> = {
  price: 'Price Change',
  uin: 'UIN / Identifier',
  web: 'Web / Listing Update',
  spec: 'Spec Change',
  design: 'Design Change',
  discontinue: 'Discontinue',
  sb2: 'Base Substrate & Dimensions',
  sb3: 'Graphics & Print',
  sb4: 'Laminate & Coating',
  sb5: 'Finishing & Hardware',
  // F-099 slice 7
  parts: 'Parts & Materials',
  artwork: 'Artwork Update',
  std_custom: 'Standard-Custom Fields',
  bundle_edit: 'Members Changed',
}

/** Map Sign-Builder section ids to aggregated change keys — reference PMAView.tsx L42–44. */
export const CHANGE_DATA_KEY: Record<string, string> = {
  sb2: 'spec',
  sb4: 'spec',
  sb5: 'spec',
  sb3: 'design',
}

export interface ChangeDetailRow {
  term: string
  detail: string
  /**
   * DD-CHG-CARD-001 — how the card renders the row: `files` = the design change's reference
   * files as attachment tiles (detail is the text fallback), `image` = detail is an image URL
   * shown as a thumbnail. Plain rows have no kind.
   */
  kind?: 'files' | 'image'
  /**
   * F-144 (slice 2, Marguerite) — a row whose detail is the NEW value of a field the product
   * already has: the card prints `current → new`. The current value comes from the line's PIM
   * record (`currentBuild`); `null` = the field is changing but the current value is unknown
   * (no record on the line), printed as "—". Rows without this key are plain facts of the
   * request (actions, notes, files, dates).
   */
  current?: string | null
}

/**
 * F-144 (slice 2) — which PIM record field answers each changeable field of the Change Request
 * card (`currentBuild` keys → DdPimRecord field keys). Only direct analogues: web title /
 * description, grommets, holes, hem, graphic method and vinyl have no single PIM column.
 */
const CURRENT_FROM_PIM: Record<string, string> = {
  label: 'label',
  price: 'price',
  uin: 'uin',
  category: 'category',
  substrate: 'stock_1',
  width: 'width',
  height: 'height',
  sides: 'single_sided_or_double_sided_print',
  finishing: 'finishing',
  rounded_corners: 'rounded_corners',
  hardware: 'hardware_information',
}

/** PIM's free-text size ("24.5 in", "24") the way the card prints the requested one: `24.5"`. */
function pimInches(raw: string): string {
  const m = raw.match(/\d+(?:\.\d+)?/)
  const n = m ? Number(m[0]) : NaN
  return Number.isFinite(n) && n > 0 ? `${n}"` : raw
}

/** PIM "Double-Sided Print" / "Single-Sided" → the wording the request's `single_double` uses. */
function pimSides(raw: string): string {
  const v = raw.trim().toLowerCase()
  if (v.startsWith('double')) return 'double'
  if (v.startsWith('single')) return 'single'
  return raw
}

/**
 * F-144 (slice 2) — the product's CURRENT values for the fields an update request can change,
 * keyed like `CURRENT_FROM_PIM`: the line's PIM record first (what PIM held when the request
 * came in — `pim_snapshot`, slice 1), then the Modify form's product snapshot for name, price,
 * UIN and category when the line has no record (requests from before the record existed).
 * Empty when neither is known. The card prints these on the left of "→".
 */
export function currentBuild(
  record: PimRecord | null | undefined,
  modifyProduct?: ModifyRequest['product']
): Record<string, string> {
  const pim: Record<string, string> = {}
  for (const group of record?.groups ?? []) {
    for (const field of group.fields) {
      const value = typeof field.value === 'string' ? field.value.trim() : ''
      if (value !== '') pim[field.key] = value
    }
  }
  const out: Record<string, string> = {}
  for (const [key, pimKey] of Object.entries(CURRENT_FROM_PIM)) {
    const raw = pim[pimKey]
    if (raw === undefined) continue
    out[key] =
      key === 'width' || key === 'height' ? pimInches(raw) : key === 'sides' ? pimSides(raw) : raw
  }
  if (modifyProduct) {
    if (!out.label && modifyProduct.label) out.label = modifyProduct.label
    if (!out.price && modifyProduct.current_price != null) {
      out.price = fmtMoney(modifyProduct.current_price)
    }
    if (!out.uin && modifyProduct.current_uin) out.uin = modifyProduct.current_uin
    if (!out.category && modifyProduct.category) out.category = modifyProduct.category
  }
  return out
}

/** A `current → new` row (none for an empty new value); current `null` = unknown, printed "—". */
function delta(
  term: string,
  detail: string | null,
  current: Record<string, string>,
  key: string
): ChangeDetailRow[] {
  return detail ? [{ term, detail, current: current[key] ?? null }] : []
}

/** A thumbnail value from the Modify form is either an image URL or free text. */
export function isImageUrl(value: string): boolean {
  const v = value.trim()
  return /^https?:\/\/\S+$/i.test(v) || (v.startsWith('/') && !/\s/.test(v))
}

function changeData(type: string, changes: Record<string, unknown>): Record<string, unknown> {
  return (changes[CHANGE_DATA_KEY[type] ?? type] ?? {}) as Record<string, unknown>
}

/** Reference PMAView.tsx productAttributeBaseline — PIM catalog + modify.product fallback. */
export function productAttributeBaseline(
  sku: string,
  modifyProduct?: ModifyRequest['product']
): Record<string, string> {
  const p = getProduct(sku)
  const out: Record<string, string> = {}
  const label = p?.label ?? modifyProduct?.label
  const productSku = p?.sku ?? modifyProduct?.sku ?? sku
  if (label) out.label = label
  if (productSku) out.sku = productSku
  if (p?.uin) out.uin = p.uin
  const price = p?.current_price ?? modifyProduct?.current_price
  if (price != null) out.price = fmtMoney(price)
  if (p?.marketplace) out.marketplace = p.marketplace
  const category = p?.category ?? modifyProduct?.category
  if (category) out.category = category
  if (p?.base_substrate) out.base_substrate = p.base_substrate
  if (p?.width != null) out.width = `${p.width}"`
  if (p?.height != null) out.height = `${p.height}"`
  return out
}

/**
 * Detail rows for one change type — reference PMAView.tsx renderChangeDetail().
 * F-144 (slice 2): `current` (see `currentBuild`) turns the rows that carry a NEW value of an
 * existing field into `current → new` rows; without it the form's product snapshot still
 * answers name / price / UIN / category, the rest print "—" on the left.
 */
export function changeDetailRows(
  type: string,
  changes: Record<string, unknown>,
  modifyProduct?: ModifyRequest['product'],
  current: Record<string, string> = currentBuild(null, modifyProduct)
): ChangeDetailRow[] {
  const c = changeData(type, changes)

  switch (type) {
    case 'price':
      return [
        ...delta('Price', c.new_price != null ? fmtMoney(Number(c.new_price)) : '—', current, 'price'),
        ...(c.effective_date ? [{ term: 'Effective', detail: String(c.effective_date) }] : []),
      ]
    case 'uin':
      return [
        { term: 'Action', detail: String(c.uin_action ?? '—') },
        ...(c.uin_action !== 'remove' ? delta('UIN', String(c.new_uin ?? '—'), current, 'uin') : []),
      ]
    case 'web':
      return [
        { term: 'Action', detail: String(c.web_action ?? '—') },
        ...(c.remove_reason ? [{ term: 'Reason', detail: String(c.remove_reason) }] : []),
        ...(c.title ? delta('Title', String(c.title), current, 'label') : []),
        ...(c.description ? [{ term: 'Description', detail: String(c.description) }] : []),
        ...(c.thumbnail
          ? [
              {
                term: 'Thumbnail',
                detail: String(c.thumbnail),
                ...(isImageUrl(String(c.thumbnail)) ? { kind: 'image' as const } : {}),
              },
            ]
          : []),
        ...(c.notes ? [{ term: 'Notes', detail: String(c.notes) }] : []),
      ]
    case 'spec':
    case 'sb2':
      return [
        ...delta('Substrate', refLabel(c.new_substrate), current, 'substrate'),
        ...(c.new_width != null ? delta('Width', `${String(c.new_width)}"`, current, 'width') : []),
        ...(c.new_height != null
          ? delta('Height', `${String(c.new_height)}"`, current, 'height')
          : []),
        ...(c.single_double ? delta('Sided', String(c.single_double), current, 'sides') : []),
      ]
    case 'design':
    case 'sb3':
      return [
        ...row('Graphic Method', refLabel(c.graphic_method)),
        ...row('Vinyl Type', refLabel(c.vinyl_type)),
        ...(c.rta_color ? [{ term: 'RTA Color', detail: String(c.rta_color) }] : []),
        ...row('White Ink', whiteInkText(c.white_ink)),
        ...(c.design_notes ? [{ term: 'Design Notes', detail: String(c.design_notes) }] : []),
        ...(c.description != null
          ? [{ term: 'Change', detail: String(c.description || '—') }]
          : []),
        ...(c.reference_sku
          ? [
              {
                term: 'Reference SKU',
                detail: String((c.reference_sku as { sku: string }).sku),
              },
            ]
          : []),
        {
          term: 'Files',
          detail: `${(c.attachments as unknown[] | undefined)?.length || 0} reference file(s)`,
          kind: 'files',
        },
      ]
    case 'sb4':
      return delta('Laminate', refLabel(c.new_finishing), current, 'finishing')
    case 'sb5':
      return [
        ...(c.taping ? [{ term: 'Taping', detail: String(c.taping) }] : []),
        ...delta('Rounded Corners', refLabel(c.rounded_corners), current, 'rounded_corners'),
        ...row('Mounting Holes', mountingHolesText(c.mounting_holes)),
        ...(c.grommets ? [{ term: 'Grommets', detail: String(c.grommets) }] : []),
        ...(c.hem ? [{ term: 'Hem', detail: String(c.hem) }] : []),
        ...(c.hardware
          ? typeof c.hardware === 'string'
            ? delta('Hardware', c.hardware, current, 'hardware')
            : [{ term: 'Hardware', detail: 'Required' }]
          : []),
      ]
    case 'sb6':
      return c.notes ? [{ term: 'Notes', detail: String(c.notes) }] : []
    case 'parts': {
      // F-099 slice 7 — the list rides on the product entry (per product), not on `changes`.
      const parts = modifyProduct?.parts
      if (!Array.isArray(parts) || parts.length === 0) {
        return [{ term: 'Parts', detail: 'Sent for another product in this request' }]
      }
      return parts.map((part, i) => ({ term: `Part ${i + 1}`, detail: modifyPartText(part, i) }))
    }
    case 'artwork': {
      const aspects = Array.isArray(c.aspects)
        ? (c.aspects as unknown[]).filter((a): a is string => typeof a === 'string')
        : []
      const design = (changes.design ?? {}) as { attachments?: unknown[] }
      return [
        {
          term: 'What changes',
          detail: aspects.length
            ? aspects.map((a) => a.charAt(0).toUpperCase() + a.slice(1)).join(', ')
            : '—',
        },
        ...(c.notes ? [{ term: 'Description', detail: String(c.notes) }] : []),
        {
          term: 'Files',
          detail: `${design.attachments?.length || 0} reference file(s)`,
          kind: 'files',
        },
      ]
    }
    case 'std_custom': {
      const fields = (c.custom_fields_definition ?? []) as {
        label: string
        allowsFile?: boolean
      }[]
      return [
        {
          term: 'Custom fields',
          detail: fields.length
            ? fields
                .map((f) => `${f.label}${f.allowsFile ? ' (file allowed)' : ''}`)
                .join(', ')
            : '—',
        },
      ]
    }
    case 'discontinue':
      return [
        { term: 'Reason', detail: String(c.reason || '—') },
        ...(c.effective_date ? [{ term: 'Effective', detail: String(c.effective_date) }] : []),
      ]
    default:
      return []
  }
}
